/**
 * Client Project Helpers
 */
import { REGEX_PROJECT_NAME } from '../../../constants/regular-expression';
import type {
	MatchingProductsFieldsFragment,
	ProductPricingDisplayEnum,
	ProjectDetailsGroupFieldsFragment,
	ProjectFieldsFragment,
	ProjectGroupDetailsFieldsFragment,
	ProjectGroupItemStatus,
	ProjectItemFieldsFragment,
	ProjectProductFinishFieldsFragment,
	ProjectProductOptionFieldsFragment,
	ProjectsQuery
} from '../../__generated__/graphql-client-types';
import { PORTFOLIO_STORAGE_KEY_PREFIX } from '../../constants/project/portfolio';
import { COLLAPSED_GROUPS_STORAGE_KEY_PREFIX } from '../../constants/project/project-details';
import type {
	CategoryFilters,
	ClonedProject,
	CloneProjectResult,
	EditProjectProductCommentError,
	EditProjectProductCommentResult,
	InternalProjectTableStyles,
	PrimaryContact,
	PrimaryContactResult,
	ProductSearchResult,
	ProjectDetailsData,
	ProjectDetailsNotFound,
	ProjectDetailsPendingGroupItem,
	ProjectDetailsResult,
	ProjectNameResult,
	ProjectMember,
	ProjectPermissions,
	ProjectProductSearch,
	ProjectSearchProduct,
	SelectableProjectGroup,
	SelectionMap
} from '../../types/project.types';

// Not adding to teamMemberDisplay to keep that object clean for API
export const teamMemberDisplayRole = (role: string): string => {
	const displayValues = {
		BUILD_REP: 'Build Rep',
		EXPERT: 'Expert',
		OWNER: 'Owner'
	};

	return displayValues[role] || 'Member';
};

export const getCommentRoleLabel = (role: string): string | null => {
	if (role === 'OWNER') return 'Owner';
	if (role === 'EXPERT') return 'Project Expert';
	return null;
};

export const isProject = (result: ProjectDetailsResult | undefined): result is ProjectDetailsData => {
	return result?.__typename === 'Project';
};

export const isProjectNotFound = (result: ProjectDetailsResult | ProjectNameResult | undefined): result is ProjectDetailsNotFound => {
	return result?.__typename === 'NotFoundError';
};

export const isCloneProjectResult = (result?: CloneProjectResult | null): result is ClonedProject => {
	return result?.__typename === 'CloneProject';
};

export const isPrimaryContactEmail = (result: PrimaryContactResult): result is PrimaryContact => {
	return 'email' in result && result.__typename === 'PrimaryContact';
};

export const shouldShowPrimaryContact = (
	isPrimaryContactEnabled: boolean,
	isInternal: boolean,
	members: ProjectMember[] | null | undefined
): members is ProjectMember[] => isPrimaryContactEnabled && isInternal && Boolean(members?.length);

export const isEditProductCommentError = (
	result: EditProjectProductCommentResult | undefined
): result is EditProjectProductCommentError => {
	return result?.__typename === 'Error';
};

export const isProductSearchResult = (result: ProjectProductSearch | null | undefined): result is ProductSearchResult => {
	return result?.__typename === 'ProductSearchResult';
};

// Returns the first product in all project groups
export const getFirstProjectProduct = (projectGroupData: ProjectDetailsGroupFieldsFragment[]): ProjectItemFieldsFragment | null => {
	for (const group of projectGroupData) {
		const { pending, purchased } = group || {};
		if (pending?.length) {
			return pending[0];
		} else if (purchased?.length) {
			return purchased[0];
		}
	}

	return null;
};

export const isShowroomOnly = (productPricingDisplay: ProductPricingDisplayEnum): boolean =>
	productPricingDisplay === 'CALL_FOR_PRICING' || productPricingDisplay === 'SHOW_ROOM_ONLY';

export const isCallForPurchase = (productPricingDisplay: ProductPricingDisplayEnum): boolean =>
	productPricingDisplay === 'CALL_FOR_PURCHASE';

export const isProductIneligibleForCart = (productPricingDisplay: ProductPricingDisplayEnum): boolean =>
	isShowroomOnly(productPricingDisplay) || isCallForPurchase(productPricingDisplay);

export const isSale = (wasPrice: number | null, unitPrice: number): boolean => Boolean(wasPrice && wasPrice > unitPrice);

export const getPriceOverrideMessage = (showroomOnly: boolean, isCustomProductWithoutPrice: boolean): string | undefined => {
	return showroomOnly ? 'Call for Price' : isCustomProductWithoutPrice ? 'Contact Consultant for Price' : undefined;
};

export const initiateDownload = (url: string, userFileName: string): void => {
	const downloadLink = document.createElement('a');
	downloadLink.href = url;
	downloadLink.setAttribute('download', userFileName);
	downloadLink.click();
};

export const projectSearchItemsMapper = (
	projectSearchItems: ProjectProductOptionFieldsFragment[] | MatchingProductsFieldsFragment[]
): ProjectSearchProduct[] => {
	return projectSearchItems.map((projectSearchItem) => {
		if ('resource' in projectSearchItem) {
			const { resource, image } = projectSearchItem;
			const { price, title, brandName, url, finishes, variantId } = resource || {};
			return {
				id: String(variantId || ''),
				image,
				price: price?.current || 0,
				title: title || '',
				url: url || '',
				brandName: brandName || '',
				variants: finishes || []
			};
		} else {
			const { title, id, url, brandName, variants = [], image, priceInfo, isConfigurable } = projectSearchItem;
			return {
				title,
				id,
				url,
				brandName,
				variants,
				image,
				price: priceInfo?.current || 0,
				isConfigurable
			};
		}
	});
};

export const isValidProjectName = (projectName: string): boolean => !REGEX_PROJECT_NAME.test(projectName);

export const getSelectedProductIds = (selection: SelectionMap) =>
	Object.values(selection).flatMap((group) => group.selectedProducts.flatMap((product) => product.itemId));

export const getSelectedProductToCopy = (
	selection: SelectionMap,
	projectGroups: ProjectDetailsGroupFieldsFragment[],
	selectedItemCount: number
): ProjectDetailsPendingGroupItem | undefined => {
	if (selectedItemCount !== 1) {
		return undefined;
	}

	const selectedGroup = Object.entries(selection).find(([, groupSelection]) => groupSelection.selectedProducts.length === 1);
	if (!selectedGroup) {
		return undefined;
	}

	const [groupId, groupSelection] = selectedGroup;
	const selectedProduct = groupSelection.selectedProducts[0];
	if (selectedProduct.isCustomProduct) {
		return undefined;
	}

	const projectGroup = projectGroups.find((group) => group.id === Number(groupId));

	return projectGroup?.[selectedProduct.purchased ? 'purchased' : 'pending']?.find((item) => item.id === selectedProduct.itemId);
};

export const mapFinishToProjectItem = (
	hoveredFinish: ProjectProductFinishFieldsFragment | null,
	currentItem: ProjectItemFieldsFragment
): ProjectItemFieldsFragment | null => {
	if (!hoveredFinish) return null;
	const { finish, id, image, inventory, price, sku } = hoveredFinish;
	return {
		...currentItem,
		finish,
		id,
		image: { ...image, photoId: null },
		stockCount: inventory,
		productId: sku,
		totalPrice: price * (currentItem.quantityPurchased || currentItem.quantityUnPurchased),
		unitPrice: price
	};
};

export const mapCategoriesAndQuantities = (groupProducts: { baseCategory: string; type?: string }[]): CategoryFilters[] => {
	const allCategories: CategoryFilters[] = [];
	for (const product of groupProducts) {
		const category = product.type === 'SPECIAL_PRODUCT' || !product.baseCategory ? 'Other' : product.baseCategory;
		const existingCategory = allCategories.find((item: CategoryFilters) => item.category === category);
		if (existingCategory) {
			existingCategory.quantity += 1;
		} else {
			allCategories.push({ category, quantity: 1 });
		}
	}
	return allCategories;
};

export const formatSelectedFilters = (selectedFiltersCategories: string[]) => {
	return selectedFiltersCategories.length > 2
		? selectedFiltersCategories
				.slice(0, 2)
				.join(', ')
				.concat(`, +${selectedFiltersCategories.length - 2}`)
		: selectedFiltersCategories.join(', ');
};

export const getReorderedProjects = (
	currentProjectId?: number,
	currentProjectData?: ProjectFieldsFragment[],
	data?: ProjectsQuery
): ProjectFieldsFragment[] | undefined => {
	const currentProject = (currentProjectId && currentProjectData) || undefined;
	const projectData = data?.projects?.projects?.filter((project) => project.id !== currentProjectId) || undefined;
	return currentProject || projectData ? [...(currentProject || []), ...(projectData || [])] : undefined;
};

export const extractCategoriesFromProject = (groupProducts: ProjectItemFieldsFragment[]): string[] => {
	const categoriesAndQuantities = mapCategoriesAndQuantities(groupProducts);
	return categoriesAndQuantities.map((category) => category.category);
};

export const shouldShowProductInclusionAlert = (
	watchIncludePurchasedItems: boolean,
	watchIncludeNonpurchasedItems: boolean,
	watchIncludedBids: string[],
	importedBidNumbers: string[],
	includedGroupsLength: number,
	availableGroupsLength: number
): boolean =>
	includedGroupsLength > 0 &&
	(includedGroupsLength < availableGroupsLength ||
		!watchIncludePurchasedItems ||
		!watchIncludeNonpurchasedItems ||
		watchIncludedBids.length !== importedBidNumbers.length);

export const getFlattenedProjectItems = (groups: ProjectDetailsGroupFieldsFragment[] = []): ProjectItemFieldsFragment[] => {
	return groups.flatMap((group) => [...(group.pending || []), ...(group.purchased || [])]);
};

export const applyFiltersToProjectGroupItems = (appliedFilters: CategoryFilters[], projectGroups: ProjectDetailsGroupFieldsFragment[]) =>
	appliedFilters.length
		? projectGroups.map((group) => ({
				...group,
				pending: group.pending
					? group.pending.filter(
							(item) =>
								appliedFilters.length === 0 ||
								appliedFilters.some(
									(filter) =>
										(item.type === 'SPECIAL_PRODUCT' && 'Other' === filter.category) ||
										item.baseCategory === filter.category
								)
					  )
					: [],
				purchased: group.purchased
					? group.purchased?.filter(
							(item) =>
								appliedFilters.length === 0 ||
								appliedFilters.some(
									(filter) =>
										(item.type === 'SPECIAL_PRODUCT' && 'Other' === filter.category) ||
										item.baseCategory === filter.category
								)
					  )
					: []
		  }))
		: projectGroups;

export const isDuplicateGroupName = (
	project: ProjectGroupDetailsFieldsFragment | undefined,
	newGroupName: string,
	groupIdOfCurrentGroup: number
): boolean =>
	project?.groups.some(
		(projectGroup) =>
			projectGroup.name.toLowerCase().trim() === newGroupName.toLowerCase().trim() && projectGroup.id !== groupIdOfCurrentGroup
	) || false;

export const getInternalStyles = (isInternalDashboard = false, showExpandedInfo = false): InternalProjectTableStyles => {
	const internalStyles = isInternalDashboard ? 'pointer bg-theme-white' : '';
	const internalTableDataClass = isInternalDashboard ? 'flex justify-end' : '';
	const internalExpandedStyles = isInternalDashboard && showExpandedInfo ? 'relative' : '';
	return { internalStyles, internalTableDataClass, internalExpandedStyles };
};

export const buildProjectGroupSources = (
	selection: SelectionMap
): {
	allItems: boolean;
	shoppingListGroupId: number;
	sourceItems: { itemId: number; status: ProjectGroupItemStatus }[] | undefined;
}[] => {
	return Object.entries(selection).map(([groupId, selectionData]) => {
		return {
			shoppingListGroupId: parseInt(groupId, 10),
			allItems: selectionData.allProductsSelected,
			sourceItems: selectionData.allProductsSelected
				? undefined
				: selectionData.selectedProducts.map(({ itemId, purchased }) => {
						const status: ProjectGroupItemStatus = purchased ? 'PURCHASED' : 'UNPURCHASED';
						return {
							itemId,
							status
						};
				  })
		};
	});
};

export const getGroupsByProjectId = (groups: SelectableProjectGroup[]): Record<number, SelectableProjectGroup[]> => {
	return groups.reduce((acc, group): Record<number, SelectableProjectGroup[]> => {
		if (!acc[group.projectId]) {
			acc[group.projectId] = [];
		}
		acc[group.projectId].push(group);
		return acc;
	}, {});
};

export const buildMoveCopyPayload = (
	groups: SelectableProjectGroup[]
): {
	targetShoppingListGroupIds: number[];
	newShoppingListGroups:
		| {
				projectId: number;
				name: string;
				displayOrder: number;
		  }[]
		| undefined;
} => {
	const existingGroupIds: number[] = [];
	const newShoppingListGroups: {
		projectId: number;
		name: string;
		displayOrder: number;
	}[] = [];

	for (const group of groups) {
		if (group.id === 0) {
			newShoppingListGroups.push({
				projectId: group.projectId,
				name: group.name,
				displayOrder: 1
			});
		} else {
			existingGroupIds.push(group.id);
		}
	}

	return {
		targetShoppingListGroupIds: existingGroupIds.length > 0 ? existingGroupIds : [],
		newShoppingListGroups: newShoppingListGroups.length > 0 ? newShoppingListGroups : undefined
	};
};

export const buildPortfolioPreferencesStorageKey = (projectId: number): string => {
	return `${PORTFOLIO_STORAGE_KEY_PREFIX}-${projectId}`;
};

export const buildCollapsedGroupsStorageKey = (projectId: number): string => {
	return `${COLLAPSED_GROUPS_STORAGE_KEY_PREFIX}-${projectId}`;
};

export const mapPermissionToRole = (permissions: ProjectPermissions): 'Collaborator' | 'Editor' | 'Commenter' | 'Viewer' => {
	const { commenting, invite, editProject, showDiscount } = permissions;
	if (commenting && invite && editProject && showDiscount) {
		return 'Collaborator';
	} else if (commenting && invite && editProject) {
		return 'Editor';
	} else if (commenting) {
		return 'Commenter';
	} else {
		return 'Viewer';
	}
};

export const mapRoleToPermission = (permissionLabel: string) => {
	switch (permissionLabel) {
		case 'Collaborator':
			return { commenting: true, invite: true, editProject: true, showDiscount: true };
		case 'Editor':
			return { commenting: true, invite: true, editProject: true, showDiscount: false };
		case 'Commenter':
			return { commenting: true, invite: false, editProject: false, showDiscount: false };
		default:
			return { commenting: false, invite: false, editProject: false, showDiscount: false };
	}
};

export const buildAlreadyInvitedMessage = (emailAddress: string, projectName: string = 'this project'): string =>
	`${emailAddress} has already been invited to ${projectName}.`;
